import { useState, useEffect, useCallback } from "react";

import { priorityDashboardService } from "../services/PriorityDashboardService";
import { revenueMetricsService } from "../services/RevenueMetricsService";

/**
 * Pilots dashboard data — sourced from TWO endpoints (BE pano #1075):
 *
 *   GET /priority-dashboard/summary/   → evaluating_mrr, active_pilots,
 *       pilots_at_risk, at_risk_mrr, decision_due_days
 *                                                (Pilot MRR / Active / MRR-at-Risk tiles)
 *   GET /metrics/team-revenue/         → pilot_conversion_rate_pct, pilot_stage_counts
 *                                                (Conversion tile + Pipeline bar)
 *
 * Both run in parallel and are returned raw; field extraction + fallbacks live in
 * `buildPilotMetrics` (Pilots.js). Resilient by design — a failure in one endpoint
 * still renders the tiles backed by the other (every field is null-safe); only a
 * both-failed case surfaces an error.
 */
export const usePilots = () => {
  const [summary, setSummary] = useState(null);
  const [teamRevenue, setTeamRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [summaryRes, teamRevenueRes] = await Promise.allSettled([
      priorityDashboardService.getSummary(),
      revenueMetricsService.getTeamRevenueMetrics(),
    ]);

    if (summaryRes.status === "fulfilled") setSummary(summaryRes.value);
    if (teamRevenueRes.status === "fulfilled")
      setTeamRevenue(teamRevenueRes.value);

    if (
      summaryRes.status === "rejected" &&
      teamRevenueRes.status === "rejected"
    ) {
      setError(
        summaryRes.reason ??
          teamRevenueRes.reason ??
          new Error("Failed to load pilots"),
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    window.addEventListener("ownerScopeChanged", fetchAll);
    return () => {
      window.removeEventListener("ownerScopeChanged", fetchAll);
    };
  }, [fetchAll]);

  return { summary, teamRevenue, loading, error };
};
